import { useMemo } from "react";

import Claimed from "@/components/claim-status/claimed";
import Partner from "@/components/claim-status/partner";
import Unclaimed from "@/components/claim-status/unclaimed";
import CloudinaryImage from "@/components/cloudinary-image";
import DirectionsIcon from "@/components/icons/directions-icon";
import LinkIcon from "@/components/icons/link-icon";
import PhoneIconSolid from "@/components/icons/phone-icon";
import type { PhotoType } from "@/components/photos-list";
import { Button } from "@/components/ui/button";
import { linkPropsWithSource } from "@/helpers/url";
import { cn } from "@/lib/utils";

import type { CompanyType, LocationType } from "./types";

const LOCATION_HERO_BUTTON_CLASS_NAME = cn(
  "flex items-center gap-1.5 rounded-full border border-white/50 bg-eerie-black px-2.5 py-1 font-medium text-12 text-white shadow-white/10 transition-[colors,shadow]",
  "hover:bg-white/10 hover:shadow-0-0-10"
);

interface HeroProps {
  company: CompanyType;
  location: LocationType;
  openGallery: (index: number) => void;
  photos: PhotoType[];
}

export default function Hero({
  company,
  location,
  photos,
  openGallery,
}: HeroProps) {
  const handleOpenGallery = () => openGallery(0);

  const links = useMemo(() => {
    const list: React.ReactNode[] = [];
    const url = location.url || company.url;

    if (url) {
      list.push(
        <Button asChild className={LOCATION_HERO_BUTTON_CLASS_NAME} key={url}>
          <a
            href={linkPropsWithSource(url).href}
            itemProp="url"
            rel="noopener noreferrer"
            target="_blank"
          >
            <LinkIcon />
            <span>Website</span>
          </a>
        </Button>
      );
    }

    if (location.phoneNumber) {
      list.push(
        <Button
          asChild
          className={LOCATION_HERO_BUTTON_CLASS_NAME}
          key={location.phoneNumber}
        >
          <a href={`tel:${location.phoneNumber}`} itemProp="url">
            <PhoneIconSolid />
            <span>Call</span>
          </a>
        </Button>
      );
    }

    const { address } = location;
    const streetNumber = address.address_components.find(({ types }) =>
      types.includes("street_number")
    );
    const googleMapsLink =
      address?.formatted_address && streetNumber
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            address.formatted_address
          )}`
        : "";

    if (googleMapsLink) {
      list.push(
        <Button
          asChild
          className={LOCATION_HERO_BUTTON_CLASS_NAME}
          key={googleMapsLink}
        >
          <a
            href={googleMapsLink}
            itemProp="hasMap"
            rel="noopener noreferrer"
            target="_blank"
          >
            <DirectionsIcon />
            <span>Directions</span>
          </a>
        </Button>
      );
    }

    return list;
  }, [company, location]);

  return (
    <div className="relative md:h-125">
      {/** biome-ignore lint/a11y/noStaticElementInteractions: dis fine */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: dis fine */}
      {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy */}
      <div
        className={cn(
          "relative top-0 right-0 aspect-375/230 w-full md:absolute md:aspect-auto md:h-full md:w-1/2 md:cursor-pointer",
          'before:absolute before:inset-0 before:z-1 before:block before:bg-linear-180 before:from-20% before:from-black/50 before:to-80% before:to-transparent before:content-[""] md:before:from-7% md:before:from-black/60 md:before:to-43%'
        )}
        onClick={handleOpenGallery}
      >
        {!!photos.length && (
          <CloudinaryImage
            alt={`Main image for ${location.displayName || location.company.name}`}
            className="size-full md:rounded-bl-[100px]"
            imageId={photos[0].imageId || ""}
            itemProp="image"
            rW={720}
          />
        )}
      </div>
      <section className="container relative flex h-full items-center">
        <div className="flex w-full flex-col md:w-1/2 md:flex-col-reverse md:gap-4.5 md:pt-20">
          {links.length > 0 && (
            <div className="-mb-4 flex -translate-y-1/2 gap-2.5 md:mb-0 md:translate-y-0">
              {links}
            </div>
          )}
          <div className="flex flex-col items-start gap-2.5 border-white/20 border-b py-6 md:gap-4.5 md:border-0 md:py-0 md:pr-12.5">
            <h1 className="text-18 leading-[1em] md:top-0 md:text-60 [&>div]:relative [&>div]:top-0.5">
              {location.displayName || location.company.name}
              {location.isPartnerLite ? (
                <>
                  {" "}
                  <Partner className="size-4.5 md:size-8.5" />
                </>
              ) : (
                location.isClaimed && (
                  <>
                    {" "}
                    <Claimed className="size-4.5 md:size-8.5" />
                  </>
                )
              )}
            </h1>
            <div className="flex flex-col gap-2.5 md:flex-row-reverse">
              {!location.isClaimed && <Unclaimed />}
              <p
                className="text-12 leading-[1.5em] md:text-16"
                itemProp="address"
              >
                {location.address.formatted_address}
                {!!location.address2 && (
                  <>
                    <br />
                    {location.address2}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
